import React, {useState} from 'react'

import socket from '../../../socketio/socketio.js'
import Button from '../buttons/Button.js'
import Modal from '../overlays/Modal.js'
import ModalHeader from '../overlays/ModalHeader.js'
import P from '../typography/P.js'
import S2 from '../typography/S2.js'
import {formSpacing, FormStack, Input, TextArea} from '../forms/index.js'
import Alert from './Alert.js'

interface ErrorReportModalProps {
    show: boolean
    /** The error shown and reported */
    err?: Error
    /** Sent along with the report */
    customMessage?: string
    /** Opens on the report form instead of the error, for a caller that shows the error itself */
    reportOnly?: boolean
    onClose?: () => void
}

/** The error with its stack, the first screen of ErrorReportModal */
function ErrorDetails({err}: {err?: Error}) {
    return (
        <>
            <P style={{marginBottom: 30}}>
                Onderstaande fout is opgetreden:
            </P>

            <div style={{width: '100%', maxHeight: 200, overflowY: 'auto', marginBottom: 12}}>
                {err &&
                    <Alert style={{marginBottom: 6}} variant='danger'>
                        <P style={{marginBottom: 12}}>{err.message}</P>
                        <S2>{err.stack}</S2>
                    </Alert>
                }
            </div>
        </>
    )
}

interface ReportFormProps {
    email: string
    comment: string
    onChangeEmail: (email: string) => void
    onChangeComment: (comment: string) => void
}

/** Email address and comment of the report ('Meld deze fout') */
function ReportForm({email, comment, onChangeEmail, onChangeComment}: ReportFormProps) {
    return (
        <>
            <P style={{marginBottom: 30}}>
                Vul je emailadres in en geef een korte toelichting.
            </P>

            <FormStack style={{marginBottom: formSpacing.rowGap}}>
                <Input
                    label='Emailadres'
                    value={email}
                    onChange={(event) => onChangeEmail(event.target.value)}
                />

                <TextArea
                    rows={4}
                    label='Toelichting'
                    value={comment}
                    onChange={(event) => onChangeComment(event.target.value)}
                />
            </FormStack>
        </>
    )
}

/**
 * Shows an error and lets the user report it ('Meld deze fout'): an email address and a comment are mailed to us
 * with the error. Used by ErrorBoundary for render errors and by Alert's errorHandler variant (reportOnly). Closing
 * calls onClose and starts over.
 */
export default function ErrorReportModal({show, err, customMessage, reportOnly, onClose}: ErrorReportModalProps) {
    const [reporting, setReporting] = useState(false)
    const [email, setEmail] = useState('')
    const [comment, setComment] = useState('')
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState('')
    const [error, setError] = useState('')

    const view = reportOnly || reporting ? 'send' : 'error'

    // Starts over before onClose, which may unmount it (ErrorBoundary)
    const close = () => {
        setReporting(false)
        setEmail('')
        setComment('')
        setLoading(false)
        setMessage('')
        setError('')

        onClose?.()
    }

    const send = () => {
        setLoading(true)
        setMessage('')
        setError('')

        const report = {
            customMessage,
            message: err?.message,
            stack: err?.stack,
            url: window.location.href
        }

        socket.emit('mail.sendErrorEmail', sessionStorage.token || localStorage.mtoken, email, comment, report, (sendError) => {
            setLoading(false)

            if (sendError) {
                setError(sendError)
            } else {
                setMessage('We hebben je bericht ontvangen, we nemen zo snel mogelijk contact op.')
            }
        })
    }

    return (
        <Modal show={show} style={{width: 400, maxWidth: '90%'}} onClose={close}>
            <ModalHeader title='Foutmelding' onClose={close} />

            {view === 'error' && <ErrorDetails err={err} />}

            {view === 'send' &&
                <ReportForm email={email} comment={comment} onChangeEmail={setEmail} onChangeComment={setComment} />
            }

            {message &&
                <Alert style={{marginBottom: 6}} variant='success'>{message}</Alert>
            }

            {error &&
                <Alert style={{marginBottom: 6}} variant='danger'>{error}</Alert>
            }

            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
                {view === 'error' && !message && !error &&
                    <Button variant='text' loading={loading} onClick={() => setReporting(true)}>
                        Meld deze fout
                    </Button>
                }

                {view === 'send' && !message && !error &&
                    <Button variant='text' loading={loading} disabled={!email || !comment} onClick={send}>
                        Verstuur
                    </Button>
                }

                {(message || error) &&
                    <Button variant='text' onClick={close}>
                        Sluiten
                    </Button>
                }
            </div>
        </Modal>
    )
}
